/* Front pocket personalization — checkout guard.
 *
 * Order #2339 (23 Sep 2026) reached checkout as a $399 bag carrying a full
 * embroidery spec, no artwork file and no service line. The customer had
 * configured personalization and then paid with Apple Pay straight from the
 * product page. Express checkout buttons never fire a form submit event: they
 * read the product form, take its hidden properties[] inputs, and go. The
 * artwork only exists as a Blob in memory and the $99 line is only added by
 * fpm.js's submit handler, so neither happened.
 *
 * This file closes two holes without touching fpm.js:
 *
 *   1. While personalization is switched on, the express buttons are hidden,
 *      so the bypass is not reachable. Plain bags keep one-tap checkout.
 *   2. If personalization is on but unfinished, add-to-cart is blocked with a
 *      message. fpm.js on its own returns early in that case and lets the
 *      native submit add a plain bag, silently.
 *
 * Readiness is read from fpm.js rather than recomputed here: fpm.js writes its
 * [data-fpm-prop] inputs into the form only when its own ready() is true, so
 * their presence IS that signal. Two copies of the rule could disagree; this
 * cannot.
 *
 * ---------------------------------------------------------------------------
 * 2 Oct 2026 — third hole, found on order #2366.
 *
 * #2366 was paid for as a white bag + $99 Front Pocket Logo Embroidery whose
 * bag line carried "Artwork File: GGG-fullcolor-logo.png" and "Artwork Source:
 * Customer upload" — but no "Artwork" property. There was no file. The order
 * cannot be produced and the customer had no idea anything was wrong.
 *
 * The cause is structural, not a failed upload. The two properties are created
 * by completely different mechanisms:
 *
 *   "Artwork File"  a plain hidden <input name="properties[Artwork File]">,
 *                   written into the product form by fpm.js's syncProps() as
 *                   soon as the configuration is locally valid.
 *   "Artwork"       a multipart part appended ONLY inside fpm.js's own
 *                   fetch(cart/add.js). It cannot exist as a hidden input —
 *                   it is a File object, not a string.
 *
 * So any add-to-cart that does NOT go through fpm.js's submit handler carries
 * the filename and loses the file. fpm.js declines the event whenever
 * `!ready() || busy`, and `busy` is set true on success and never cleared
 * before the redirect — so a second click during that window falls straight
 * through to a native submit of the hidden inputs alone.
 *
 * The fix below does not try to re-derive when fpm.js will act. It observes
 * whether fpm.js actually DID act: fpm.js calls stopImmediatePropagation() in
 * a capture-phase listener on document, so when it handles a submit the event
 * never reaches the bubble phase. A bubble-phase listener on window therefore
 * runs only when fpm.js declined — and in that case the artwork is not
 * attached, so the add must be stopped.
 *
 * This is deliberately fail-closed. If fpm.js throws, fails to load, or is
 * mid-flight, a personalized add is refused rather than taken. A refused sale
 * costs a reload; an accepted one costs a paid order that cannot be made.
 */
(function () {
  "use strict";

  var CLS = "fpm-personalizing";

  // display:none on the host also hides shadow-DOM children, which is where
  // Shopify renders the actual Apple Pay / Google Pay buttons.
  var CSS =
    "html." + CLS + " .shopify-payment-button," +
    "html." + CLS + " shopify-accelerated-checkout," +
    "html." + CLS + " shopify-accelerated-checkout-cart," +
    "html." + CLS + " [data-shopify=dynamic-checkout-cart]" +
    "{display:none !important}";

  function injectCss() {
    if (document.querySelector("style[data-fpm-guard]")) return;
    var s = document.createElement("style");
    s.setAttribute("data-fpm-guard", "");
    s.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(s);
  }

  function toggleEl() { return document.querySelector("[data-fpm-on]"); }
  function isOn() { var t = toggleEl(); return !!(t && t.checked); }

  function cartForm() { return document.querySelector('form[action*="/cart/add"]'); }

  // fpm.js writes these only when its ready() passes. No prop inputs, not ready.
  function isReady() {
    var f = cartForm();
    return !!(f && f.querySelector("[data-fpm-prop]"));
  }

  // A personalized configuration is staged in this form. Keyed off the generic
  // prop marker rather than any single property: as of 2 Oct 2026 fpm.js no
  // longer writes "Artwork File" as a hidden input (it now travels with the
  // file in the multipart request), so keying off that name would silently stop
  // matching.
  function hasStagedPersonalization(form) {
    var f = form || cartForm();
    return !!(f && f.querySelector("[data-fpm-prop]"));
  }

  function isCartAdd(form) {
    return !!(form && form.action && form.action.indexOf("/cart/add") !== -1);
  }

  function setClass(on) {
    var r = document.documentElement;
    if (r.classList) { r.classList.toggle(CLS, on); return; }
    var c = (" " + r.className + " ").replace(" " + CLS + " ", " ").replace(/\s+/g, " ").trim();
    r.className = on ? (c ? c + " " + CLS : CLS) : c;
  }

  function sync() { setClass(isOn()); }

  function showError(msg) {
    var e = document.querySelector("[data-fpm-error]");
    if (!e) { if (msg) window.alert(msg); return; }
    e.textContent = msg;
    if (msg) e.removeAttribute("hidden"); else e.setAttribute("hidden", "");
  }

  function focusPanel() {
    var panel = document.querySelector("[data-fpm-panel]") || toggleEl();
    if (panel && panel.scrollIntoView) {
      try { panel.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
    }
  }

  // Capture on window runs ahead of fpm.js's own capture listener on document,
  // so an unfinished order is stopped before anything else sees the event.
  window.addEventListener("submit", function (e) {
    var form = e.target;
    if (!isCartAdd(form)) return;
    if (!isOn() || isReady()) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    showError("Add the text you want stitched or upload your logo before adding to cart — or switch personalization off to order a plain bag.");
    focusPanel();
  }, true);

  // Bubble on window runs LAST. fpm.js stops propagation in its capture-phase
  // handler whenever it takes the submit, so reaching here means it declined:
  // it was busy, it errored, or it never loaded. For a personalized order that
  // means the artwork file is not attached to this request — the filename would
  // go through on its own and produce an unmakeable order. Refuse it.
  window.addEventListener("submit", function (e) {
    var form = e.target;
    if (!isCartAdd(form)) return;
    if (e.defaultPrevented) return;
    if (!isOn() && !hasStagedPersonalization(form)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    showError("We could not attach your personalization to this order, so we have stopped it rather than make your bag without it. Please reload the page and try again — nothing has been charged.");
    focusPanel();

    if (window.console && console.warn) {
      console.warn("[fpm-guard] blocked a personalized add-to-cart that bypassed fpm.js — artwork would not have been attached");
    }
  }, false);

  document.addEventListener("change", function (e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute("data-fpm-on")) sync();
  }, true);

  injectCss();
  sync();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { injectCss(); sync(); });
  }

  // Shopify injects the wallet buttons after first paint, and the theme
  // re-renders the buy-buttons block on variant change. A cheap poll keeps the
  // gate correct through both without wiring into theme internals.
  var ticks = 0;
  var t = setInterval(function () {
    sync();
    if (++ticks > 60) clearInterval(t);
  }, 500);
  document.addEventListener("shopify:section:load", sync);
})();
